import { jsPDF } from "jspdf";
import { inr } from "./roi";
import { drawFreshEarthSeal } from "./pdfLogo";

// Brand palette
const FOREST = [26, 43, 34];
const TERRA = [184, 101, 81];
const BONE = [247, 246, 240];
const INK = [28, 28, 28];
const SLATE = [92, 94, 85];
const STONE = [138, 140, 130];

export function generateRoiPdf(r) {
  const doc = new jsPDF({ unit: "pt", format: "a4" }); // 595 x 842 pt
  const W = 595;
  const M = 48;

  // ---- Header band ----
  doc.setFillColor(...FOREST);
  doc.rect(0, 0, W, 120, "F");
  drawFreshEarthSeal(doc, { x: M, y: 28, size: 64, tone: "light" });
  doc.setTextColor(...BONE);
  doc.setFont("times", "italic");
  doc.setFontSize(24);
  doc.text("Fresh Earth", M + 76, 60);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(220, 224, 214);
  doc.text("OED — Odour Eliminator & Decomposer", M + 76, 78);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...TERRA);
  doc.text("ROI SNAPSHOT", W - M, 52, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setTextColor(220, 224, 214);
  doc.text(new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }), W - M, 68, { align: "right" });

  // ---- Title ----
  let y = 168;
  doc.setTextColor(...INK);
  doc.setFont("times", "normal");
  doc.setFontSize(26);
  doc.text("Estimated impact of an OED programme", M, y);
  y += 22;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...SLATE);
  doc.text("Prepared for your site parameters. Figures are indicative, based on Fresh Earth field deployments.", M, y);

  // ---- Inputs ----
  y += 40;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...STONE);
  doc.text("YOUR INPUTS", M, y);
  y += 8;
  doc.setDrawColor(210, 210, 200);
  doc.line(M, y, W - M, y);
  y += 22;

  const inputRows = [
    ["Application", r.application],
    ["Waste throughput", `${r.tonnesPerDay} tonnes / day`],
    ["Dosage", `${r.dosage} L / tonne`],
    ["Current monthly odour spend", `₹ ${inr(r.monthlySpend)}`],
  ];
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  inputRows.forEach(([k, v]) => {
    doc.setTextColor(...SLATE);
    doc.text(k, M, y);
    doc.setTextColor(...INK);
    doc.text(String(v), W - M, y, { align: "right" });
    y += 22;
  });

  // ---- Result metric cards ----
  y += 18;
  const cards = [
    ["Odour control", `${r.odour}%`, "within a week"],
    ["Weight reduction", r.reduction ? `${r.reduction}%` : "—", "of treated waste"],
    r.biogas
      ? ["Biogas increase", `+${r.biogas}%`, "observed"]
      : ["Decomposition", r.speed, "faster vs natural"],
  ];
  const cardW = (W - 2 * M - 2 * 14) / 3;
  cards.forEach((c, i) => {
    const x = M + i * (cardW + 14);
    doc.setFillColor(235, 233, 224);
    doc.roundedRect(x, y, cardW, 84, 8, 8, "F");
    doc.setFont("times", "normal");
    doc.setFontSize(28);
    doc.setTextColor(...FOREST);
    doc.text(String(c[1]), x + 14, y + 42);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...STONE);
    doc.text(String(c[0]).toUpperCase(), x + 14, y + 60);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...SLATE);
    doc.text(String(c[2]), x + 14, y + 72);
  });

  // ---- Headline savings band ----
  y += 108;
  doc.setFillColor(...TERRA);
  doc.roundedRect(M, y, W - 2 * M, 92, 10, 10, "F");
  doc.setTextColor(...BONE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("ESTIMATED ANNUAL VALUE / SAVINGS", M + 20, y + 28);
  doc.setFont("times", "normal");
  doc.setFontSize(34);
  doc.text(`₹ ${inr(r.annualNetValue)}`, M + 20, y + 66);

  // ---- Economics breakdown ----
  y += 118;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...STONE);
  doc.text("PER-TONNE ECONOMICS", M, y);
  y += 8;
  doc.setDrawColor(210, 210, 200);
  doc.line(M, y, W - M, y);
  y += 22;
  const econ = [
    ["Treatment cost", `₹ ${inr(r.costPerTonne)} / tonne`],
    ["Recovered material value", `₹ ${inr(r.revenuePerTonne)} / tonne`],
    ["Net value", `₹ ${inr(r.netPerTonne)} / tonne`],
  ];
  doc.setFontSize(11);
  econ.forEach(([k, v], idx) => {
    doc.setFont("helvetica", idx === 2 ? "bold" : "normal");
    doc.setTextColor(...(idx === 2 ? INK : SLATE));
    doc.text(k, M, y);
    doc.text(v, W - M, y, { align: "right" });
    y += 22;
  });

  // ---- Footer ----
  doc.setDrawColor(210, 210, 200);
  doc.line(M, 792, W - M, 792);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...STONE);
  doc.text("Fresh Earth · OED  |  +91 98995 34141  |  care@fresh-earth.com", M, 808);
  doc.text("Deployed across 15 municipal corporations · ISO 14001:2015", M, 820);
  doc.text("Indicative estimate — not a commercial quote.", W - M, 808, { align: "right" });

  doc.save(`FreshEarth-OED-ROI-${r.tonnesPerDay}tpd.pdf`);
}
